import { formatValidQueryParamUrlCommaSeparated } from "utils/common";
import { sunoCommsApi } from "../api-base";
import type {
  BulkTagConfigurationRequest,
  BulkTagConfigurationResponse
} from "../types/bulk-tag-configuration.type";
import type {
  LeadsAssignedTag,
  LeadsAssignedTagCreate,
  LeadsAssignedTagFilter,
  LeadsAssignedTagUpdate
} from "../types/leads-assigned-tag.type";
import type {
  LeadsTag,
  LeadsTagCreate,
  LeadsTagFilter,
  LeadsTagUpdate
} from "../types/leads-tag.type";
import type {
  TagRule,
  TagRuleCreateInput,
  TagRuleFilter,
  TagRuleUpdateInput
} from "../types/tag-rules.type";

export const tagsEndpoints = sunoCommsApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    getTags: builder.query<LeadsTag[], LeadsTagFilter | void>({
      query: (filter = {}) =>
        `/tags${formatValidQueryParamUrlCommaSeparated(
          { ...filter, limit: filter?.limit ?? 300 },
          true
        )}`,
      providesTags: ["Tags", "AssignedTags"]
    }),
    getTag: builder.query<LeadsTag, string>({
      query: (id) => `/tags/${id}`,
      providesTags: (_result, _error, id) => [{ type: "Tags" as const, id }]
    }),
    createTag: builder.mutation<LeadsTag, LeadsTagCreate>({
      query: (body) => ({
        url: "/tags/",
        method: "POST",
        body
      }),
      invalidatesTags: ["Tags", "AssignedTags"]
    }),
    updateTag: builder.mutation<LeadsTag, LeadsTagUpdate & { id: string }>({
      query: ({ id, ...body }) => ({
        url: `/tags/${id}`,
        method: "PATCH",
        body
      }),
      invalidatesTags: ["Tags", "AssignedTags"]
    }),
    deleteTag: builder.mutation<void, string>({
      query: (id) => ({
        url: `/tags/${id}`,
        method: "DELETE"
      }),
      invalidatesTags: ["Tags", "AssignedTags"]
    }),
    getTagRules: builder.query<TagRule[], TagRuleFilter | void>({
      query: (filter = {}) =>
        `/tag-rules${formatValidQueryParamUrlCommaSeparated(
          { ...filter },
          true
        )}`,
      providesTags: ["TagRules"]
    }),
    createTagRule: builder.mutation<TagRule, TagRuleCreateInput>({
      query: (body) => ({
        url: "/tag-rules",
        method: "POST",
        body
      }),
      invalidatesTags: ["TagRules"]
    }),
    updateTagRule: builder.mutation<TagRule, TagRuleUpdateInput>({
      query: ({ id, ...body }) => ({
        url: `/tag-rules/${id}`,
        method: "PATCH",
        body
      }),
      invalidatesTags: ["TagRules"]
    }),
    deleteTagRule: builder.mutation<void, string>({
      query: (id) => ({
        url: `/tag-rules/${id}`,
        method: "DELETE"
      }),
      invalidatesTags: ["TagRules"]
    }),
    bulkCreateTagConfiguration: builder.mutation<
      BulkTagConfigurationResponse,
      BulkTagConfigurationRequest
    >({
      query: (body) => ({
        url: "/tags/bulk",
        method: "POST",
        body
      }),
      invalidatesTags: ["Tags", "TagRules"]
    }),
    getAssignedTags: builder.query<
      LeadsAssignedTag[],
      LeadsAssignedTagFilter | void
    >({
      query: (filter = {}) =>
        `/assigned-tags/${formatValidQueryParamUrlCommaSeparated(filter, true)}`,
      providesTags: ["AssignedTags"]
    }),
    addAssignedTag: builder.mutation<LeadsAssignedTag, LeadsAssignedTagCreate>({
      query: (body) => ({
        url: "/assigned-tags/",
        method: "POST",
        body
      }),
      invalidatesTags: ["AssignedTags"]
    }),
    updateAssignedTag: builder.mutation<
      LeadsAssignedTag,
      LeadsAssignedTagUpdate & { id: string }
    >({
      query: ({ id, ...body }) => ({
        url: `/assigned-tags/${id}`,
        method: "PATCH",
        body
      }),
      invalidatesTags: ["AssignedTags"]
    }),
    removeAssignedTag: builder.mutation<void, string>({
      query: (id) => ({
        url: `/assigned-tags/${id}`,
        method: "DELETE"
      }),
      invalidatesTags: ["AssignedTags"]
    })
  })
});

export const {
  useGetTagsQuery,
  useGetTagQuery,
  useCreateTagMutation,
  useUpdateTagMutation,
  useDeleteTagMutation,
  useGetTagRulesQuery,
  useCreateTagRuleMutation,
  useUpdateTagRuleMutation,
  useDeleteTagRuleMutation,
  useBulkCreateTagConfigurationMutation,
  useGetAssignedTagsQuery,
  useAddAssignedTagMutation,
  useUpdateAssignedTagMutation,
  useRemoveAssignedTagMutation
} = tagsEndpoints;
